import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDayOfWeek } from "./enums/enumDayOfWeek.generated";

// Model Objects

export class Courier extends BaseModelObject<Courier> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Lead Time in Days.
    * Not Negative
    */
    public leadTimeDays: number = 0;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Array of days of week on which orders are typically despatched. Will always read in order Monday to Sunday.
    */
    public despatchDays: EnumDayOfWeek[] = [];
    public isHidden: boolean = false;
    private _deliveryServiceId: Guid | null = null;
    /**
    * Delivery Service, if any, to apply by default to orders for delivery by this courier. May be overriden by settings at outlet or group level.
    */
    public get deliveryServiceId(): Guid | null { return this._deliveryServiceId }
    public set deliveryServiceId(value: Guid | null) {
      this._deliveryServiceId = value === undefined ? null : value;
    }
    /**
    * Shows whether DispatchCutOffTime is for the same day or next day.If true, order placed before DispatchCutOffTime is dispatched on the same day else the next day.
    */
    public isSameDayDispatchCutOffTime: boolean = false;
    private _dispatchCutOffTime: LocalTime | null = null;
    /**
    * Cut off time for placing an order. Used to calculate dispatch date for this courier.
    */
    public get dispatchCutOffTime(): LocalTime | null { return this._dispatchCutOffTime }
    public set dispatchCutOffTime(value: LocalTime | null) {
      this._dispatchCutOffTime = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.leadTimeDays = jsonSerializedObject.leadTimeDays;
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.despatchDays = jsonSerializedObject.despatchDays.map((v: any) => EnumDayOfWeek.getByValue(v));
          this.isHidden = jsonSerializedObject.isHidden;
          this.deliveryServiceId = jsonSerializedObject.deliveryServiceId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryServiceId);
          this.isSameDayDispatchCutOffTime = jsonSerializedObject.isSameDayDispatchCutOffTime;
          this.dispatchCutOffTime = jsonSerializedObject.dispatchCutOffTime == null ? null : LocalTime.parse(jsonSerializedObject.dispatchCutOffTime);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.leadTimeDays = this.leadTimeDays;
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.despatchDays = this.despatchDays.map(v => v.value);
        toRet.isHidden = this.isHidden;
        toRet.deliveryServiceId = this.deliveryServiceId == null ? null : this.deliveryServiceId.toString();
        toRet.isSameDayDispatchCutOffTime = this.isSameDayDispatchCutOffTime;
        toRet.dispatchCutOffTime = this.dispatchCutOffTime == null ? null : this.dispatchCutOffTime.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Courier.Id cannot be empty"},
            {field: "leadTimeDays", rule: (v: any) => v == null || v >= 0 || "LeadTimeDays is not greater than minimum allowed value: 0"},
            {field: "leadTimeDays", rule: (v: any) => v == null || v <= 2147483647 || "LeadTimeDays is above maximum allowed value: 2147483647"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "deliveryServiceId", rule: (v: any) => v == null || !v.isEmpty() || "Courier.DeliveryServiceId cannot be empty"},
    ];
}


export class CourierDistributionDates extends ModelObject<CourierDistributionDates> {

    public id: Guid = Guid.createEmpty();
    public dispatchDate: LocalDate = LocalDate.ofEpochDay(0);
    public deliveryDate: LocalDate = LocalDate.ofEpochDay(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.dispatchDate = LocalDate.parse(jsonSerializedObject.dispatchDate);
          this.deliveryDate = LocalDate.parse(jsonSerializedObject.deliveryDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.dispatchDate = this.dispatchDate.toString();
        toRet.deliveryDate = this.deliveryDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "CourierDistributionDates.Id cannot be empty"},
    ];
}


export class CourierService extends ServiceBase {

  public async getAllCouriers(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Courier[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.couriers.map((v: any) => new Courier(v));;
    return this.makeJsonRequest("courier/v1/getAllCouriers", req, false, extractor, opts);
  };

  public async getCourier(tenantId: Guid, courierId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Courier> {
    const req = {
    tenantId:tenantId.toString(),
    courierId:courierId.toString(),
    };
    const extractor = (response:any) => new Courier(response.courier);;
    return this.makeJsonRequest("courier/v1/getCourier", req, false, extractor, opts);
  };

  public async getCouriers(tenantId: Guid, courierIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Courier[]> {
    const req = {
    tenantId:tenantId.toString(),
    courierIds:courierIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.couriers.map((v: any) => new Courier(v));;
    return this.makeJsonRequest("courier/v1/getCouriers", req, false, extractor, opts);
  };

  public async saveCouriers(tenantId: Guid, couriers: readonly Courier[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    couriers:couriers.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("courier/v1/saveCouriers", req, true, extractor, opts);
  };

  public async setCourierIsHidden(tenantId: Guid, courierId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    courierId:courierId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("courier/v1/setCourierIsHidden", req, true, extractor, opts);
  };

  public async calculateCourierDistributionDates(tenantId: Guid, courierId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CourierDistributionDates> {
    const req = {
    tenantId:tenantId.toString(),
    courierId:courierId.toString(),
    };
    const extractor = (response:any) => new CourierDistributionDates(response.distributionDates);;
    return this.makeJsonRequest("courier/v1/calculateCourierDistributionDates", req, false, extractor, opts);
  };
}

export const courierService = new CourierService();